import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { parseJsonObject } from './json'
import { listQuery } from './query'

const api = clientFor('/nos/api')

/**
 * 仿真状态机（spec §6.1）：draft → running → done/failed。
 * 只有 draft / done 能重跑，后端对 running / failed 的 run 返 409。
 */
export type SimulationStatus = 'draft' | 'running' | 'done' | 'failed'

/** 仿真场景：与后端 internal/simulation/model.go 的 Scenarios 同源两份 */
export type SimulationScenario = 'site_layout' | 'lane_optimize' | 'cost_compare'

/** 任务级动作，与后端 PUT /simulations/:id、POST /:id/run、DELETE /:id 一一对应 */
export type SimulationAction = 'edit' | 'run' | 'delete'

/**
 * 一个仿真任务。字段与 services/nos/internal/simulation/model.go 的 Simulation 一一对应。
 *
 * 刻意注意 params / result 是 **JSON 文本字符串**（不是嵌套对象）：
 * 服务端存的就是紧凑 JSON 文本，直接 JSON.parse 用，别指望 axios 自动解。
 */
export interface Simulation {
  id: number
  tenant_id: number
  /** 仿真单号，前缀 FZ */
  doc_no: string
  name: string
  scenario: SimulationScenario
  /** 算法入参的 JSON 文本（服务端归一后的紧凑串），未填时是 "{}" */
  params: string
  /** 算法输出的 JSON 文本；未运行为空串，failed 时是 {"error":"…"} */
  result: string
  status: SimulationStatus
  finished_at: string | null
  remark: string
  created_at: string
  updated_at: string
}

/** 算法入参（与后端 simulation.Params 同字段）。三种场景共用一个结构，未用到的字段忽略。 */
export interface SimulationParams {
  /** 参与仿真的网点集合；缺省取本租户全部启用网点 */
  site_ids?: number[]
  /** 参与仿真的路径集合（现网口径）；缺省取全部启用路径 */
  lanes?: number[]
  /** cost_compare 的候选路径；缺省表示不启用任何候选路径 */
  candidate_lane_ids?: number[]
  /** 需求系数，缺省 1.0，后端限制 0 < x <= 100 */
  demand_factor?: number
  /** site_layout 各城市的日均件量；缺省按「需求系数 × 该城市网点平均能力」推算 */
  city_demand?: Record<string, number>
}

/**
 * 建档 / 编辑入参：doc_no 与 status 由服务端处理，都不在入参里。
 * params 后端收 json.RawMessage，直接送对象即可（后端也兼容字符串形式）。
 */
export interface SimulationInput {
  name: string
  scenario: SimulationScenario
  params?: SimulationParams
  remark?: string
}

export interface SimulationQuery extends PageQuery {
  keyword?: string
  scenario?: string
  status?: string
}

/** 城市需求的一行：city 与 daily 件量，未填的行整行剔掉 */
export interface CityDemandRow {
  city: string
  demand: number | undefined
}

/** 表单态：空文本用 ''，未选的 id 列表用空数组，未填的数值用 undefined */
export interface SimulationForm {
  name: string
  scenario: SimulationScenario
  site_ids: number[]
  lanes: number[]
  candidate_lane_ids: number[]
  demand_factor: number | undefined
  city_demand: CityDemandRow[]
  remark: string
}

/** 仿真场景字典，顺序与后端 simulation.Scenarios 一致，即前端下拉的展示顺序 */
export const SCENARIOS: SimulationScenario[] = ['site_layout', 'lane_optimize', 'cost_compare']

/** 与后端 services/nos/internal/simulation/model.go 的 ScenarioNames 同源两份 */
export const SCENARIO_LABELS: Record<SimulationScenario, string> = {
  site_layout: '网点布局覆盖',
  lane_optimize: '路径优化（最小生成树）',
  cost_compare: '现网与候选路径成本对比',
}

/** 与后端 services/nos/internal/simulation/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<SimulationStatus, string> = {
  draft: '待运行',
  running: '运行中',
  done: '已完成',
  failed: '已失败',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<SimulationStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    draft: 'info',
    running: 'warning',
    done: 'success',
    failed: 'danger',
  }

/** 场景 → 标签色，让三种场景在列表里一眼可分 */
export const SCENARIO_TAG: Record<SimulationScenario, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    site_layout: 'primary',
    lane_optimize: 'success',
    cost_compare: 'warning',
  }

/**
 * 各状态允许的动作，抄后端 internal/simulation/service.go：
 * 只有 draft 能编辑（done/failed 改完参数与已落库的结果对不上，后端 409）；
 * draft / done 能运行；running 不能删（算法还在跑，删掉结果无处落地）。
 */
const ACTIONS: Record<SimulationStatus, SimulationAction[]> = {
  draft: ['edit', 'run', 'delete'],
  running: ['delete'],
  done: ['run', 'delete'],
  failed: ['delete'],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: SimulationStatus, action: SimulationAction): boolean {
  return ACTIONS[status].includes(action)
}

/** ---------- 算法输出（字段与 internal/simulation/algo.go 的输出结构一一对应）---------- */

/** lane_optimize：最小生成树里选中的一条边 */
export interface MstEdge {
  lane_id: number
  lane_code: string
  mode: string
  from_site_id: number
  to_site_id: number
  distance_km: number
  cost_per_km: number
  cost: number
}

/** lane_optimize 的输出：总里程 / 估算成本 / 边列表 */
export interface MstResult {
  scenario: 'lane_optimize'
  site_count: number
  edge_count: number
  total_km: number
  est_cost: number
  demand_factor: number
  edges: MstEdge[]
}

/** cost_compare 里一侧网络的成本口径（现网 / 启用候选路径后） */
export interface CostLeg {
  lane_count: number
  total_km: number
  total_cost: number
  avg_cost_per_km: number
}

/** cost_compare 的输出：现网 vs 启用后，saving 为正表示更便宜 */
export interface CostCompareResult {
  scenario: 'cost_compare'
  demand_factor: number
  candidate_lane_ids: number[]
  current: CostLeg
  proposed: CostLeg
  saving: number
  saving_ratio: number
}

/** site_layout 里一个城市的布局结论 */
export interface CityLayout {
  city: string
  site_count: number
  capacity: number
  demand: number
  /** 负载 = 需求 / 能力，> 1 即瓶颈 */
  load_ratio: number
  coverage_ratio: number
  warn: boolean
}

/** site_layout 里一个负载 > 1 的瓶颈网点 */
export interface Bottleneck {
  site_id: number
  site_code: string
  site_name: string
  city: string
  capacity: number
  demand: number
  load_ratio: number
}

/** site_layout 的输出：按 city 的覆盖率与瓶颈 */
export interface LayoutResult {
  scenario: 'site_layout'
  demand_factor: number
  site_count: number
  city_count: number
  total_capacity: number
  total_demand: number
  coverage_ratio: number
  warn_count: number
  cities: CityLayout[]
  bottlenecks: Bottleneck[]
}

/** failed 时 result 的形状：只有 error */
export interface FailedResult {
  error: string
}

/** 三种场景的输出联合 */
export type SimulationResult = MstResult | CostCompareResult | LayoutResult | FailedResult

/**
 * 解析 result 文本：空串 / 坏 JSON / 非对象一律返回 null（页面按「未运行」渲染）。
 * 后端存的是文本，库里也可能被手改过，解析绝不能抛。
 */
export function parseResult(text: string | null | undefined): SimulationResult | null {
  return parseJsonObject<SimulationResult>(text)
}

/** 解析 params 文本：同样容错，坏数据按「没填参数」处理 */
export function parseParams(text: string | null | undefined): SimulationParams | null {
  return parseJsonObject<SimulationParams>(text)
}

/** failed 的失败原因；不是失败（或没有结果）返回空串 */
export function resultError(result: SimulationResult | null): string {
  if (!result) return ''
  const err = (result as FailedResult).error
  return typeof err === 'string' ? err : ''
}

/**
 * 结果自带的 scenario 字面量（算法输出里都带，非结构化结果没有）。
 * 页面按它分派卡片：三种场景的字段完全不同（spec §6.1），不能只显示一个总数。
 * 库里被手改过导致 scenario 不是这三个值时返回 null，页面退回「无法解析」。
 */
export function resultScenario(result: SimulationResult | null): SimulationScenario | null {
  const value = (result as { scenario?: unknown } | null)?.scenario
  if (typeof value !== 'string') return null
  return SCENARIOS.includes(value as SimulationScenario) ? (value as SimulationScenario) : null
}

/** 结果卡片是否可渲染（未运行 / failed 只有一段文字，没有结构化卡片） */
export function hasResult(result: SimulationResult | null): boolean {
  if (!result) return false
  if (resultError(result)) return false
  return resultScenario(result) !== null
}

/** 表单校验：抄后端 internal/simulation/service.go 的 normalizeInput + params.go 的取值限制 */
export function simulationFormError(form: SimulationForm): string {
  const name = form.name.trim()
  if (!name) return '请输入仿真任务名称'
  if ([...name].length > 128) return '仿真任务名称不能超过 128 个字符'
  if ([...form.remark.trim()].length > 255) return '备注不能超过 255 个字符'
  const factor = form.demand_factor
  if (factor !== undefined && !Number.isNaN(factor)) {
    if (factor <= 0 || factor > 100) return '需求系数必须在 0 到 100 之间'
  }
  for (const row of form.city_demand) {
    const city = row.city.trim()
    if (!city && row.demand === undefined) continue
    if (!city) return '城市需求的「城市」不能为空'
    if (row.demand !== undefined && !Number.isNaN(row.demand) && row.demand < 0) {
      return `城市「${city}」的日均件量不能为负数`
    }
  }
  return ''
}

/**
 * 表单 → 请求体。params 只放上面 5 个键：后端 decodeParams 开了 DisallowUnknownFields，
 * 多送一个键就是 422（静默丢字段比报错更坑）。
 * 没填的字段一律不进 params（后端 omitempty 的语义：缺省 = 全量启用网点 / 全量启用路径）。
 */
export function simulationInput(form: SimulationForm): SimulationInput {
  const input: SimulationInput = {
    name: form.name.trim(),
    scenario: form.scenario,
  }
  const remark = form.remark.trim()
  if (remark) input.remark = remark

  const params: SimulationParams = {}
  if (form.site_ids.length > 0) params.site_ids = [...form.site_ids]
  if (form.lanes.length > 0) params.lanes = [...form.lanes]
  if (form.candidate_lane_ids.length > 0) params.candidate_lane_ids = [...form.candidate_lane_ids]
  const factor = form.demand_factor
  if (factor !== undefined && !Number.isNaN(factor)) params.demand_factor = Number(factor)
  const cityDemand: Record<string, number> = {}
  for (const row of form.city_demand) {
    const city = row.city.trim()
    if (!city) continue
    if (row.demand === undefined || Number.isNaN(row.demand)) continue
    cityDemand[city] = Number(row.demand)
  }
  if (Object.keys(cityDemand).length > 0) params.city_demand = cityDemand

  // 整份 params 空着就不送这个键：后端 NormalizeParams 对缺省按 "{}" 处理，
  // 显式送 {} 与不送等价，但不送能保持请求体最小。
  if (Object.keys(params).length > 0) input.params = params
  return input
}

/** 表单 → 编辑用的 params（回显时用，与提交口径一致） */
export function formFromSimulation(
  sim: Simulation,
  siteIds: number[] = [],
  lanes: number[] = [],
  candidates: number[] = [],
): SimulationForm {
  const params = parseParams(sim.params)
  const cityDemand: CityDemandRow[] = Object.entries(params?.city_demand ?? {}).map(
    ([city, demand]) => ({ city, demand }),
  )
  const remark = sim.remark ?? ''
  return {
    name: sim.name,
    scenario: sim.scenario,
    // params 里的 id 未必都在当前参照列表里（参照只取前 200 条），
    // 这里以 params 为准补齐，避免回显时把已选的值悄悄丢掉。
    site_ids: mergeIds(params?.site_ids, siteIds),
    lanes: mergeIds(params?.lanes, lanes),
    candidate_lane_ids: mergeIds(params?.candidate_lane_ids, candidates),
    demand_factor: params?.demand_factor,
    city_demand: cityDemand,
    remark,
  }
}

/** params 里的 id 优先，参照列表里独有的 id 追加在后（保持稳定顺序） */
function mergeIds(fromParams: number[] | undefined, reference: number[]): number[] {
  const out: number[] = []
  for (const id of fromParams ?? []) if (!out.includes(id)) out.push(id)
  for (const id of reference) if (!out.includes(id)) out.push(id)
  return out
}

export type SimulationRow = Simulation

export const listSimulations = (q: SimulationQuery): Promise<PageResult<SimulationRow>> =>
  api.get<PageResult<SimulationRow>>('/simulations', listQuery({ ...q }))

export const getSimulation = (id: number): Promise<SimulationRow> =>
  api.get<SimulationRow>(`/simulations/${id}`)

export const createSimulation = (input: SimulationInput): Promise<SimulationRow> =>
  api.post<SimulationRow>('/simulations', input)

/** 编辑草稿：后端只放行 draft，其余状态 409。权限码挂 nos:sim:create（spec §6.2 没有 sim:update） */
export const updateSimulation = (id: number, input: SimulationInput): Promise<SimulationRow> =>
  api.put<SimulationRow>(`/simulations/${id}`, input)

/**
 * 运行：draft / done 才放行。算法跑挂了后端仍返 200（任务自己落到 failed、
 * 原因写进 result.error），所以「请求成功」不等于「仿真成功」，页面按返回体的 status 展示。
 */
export const runSimulation = (id: number): Promise<SimulationRow> =>
  api.post<SimulationRow>(`/simulations/${id}/run`, {})

export const deleteSimulation = (id: number): Promise<void> => api.del<void>(`/simulations/${id}`)
